import {
  GetArticlesDocument,
  Locale,
  type GetArticlesQuery,
} from '@seek/cmsu-cms-connect';
import type { LoaderFunction } from 'react-router';

import { getLocales } from '../client/shared/utils/getLocales';
import { getHygraphLocales } from '../client/shared/utils/helper';
import {
  getArticleFromData,
  getPreviewProps,
  getRouteInfo,
  getSectionQuery,
  isContentNA,
} from '../helpers/loadersHelper';
import { loadDatalabData } from '../loadersTAL';
import { internalError, notFound } from '../responses';
import type { RouteProps } from '../routes';
import { json } from '../server/utils/json';

import { getAboutSubForm_ContactUs } from './forms';

/**
 * This loader is used to fetch an article content from HyGraph.
 * @param config
 * @param client
 */
export const articleLoader =
  (
    { config, client, clientTAL }: RouteProps,
    isArticlePreview = false,
  ): LoaderFunction =>
  async ({ request, params }) => {
    // Extract variables
    const {
      site,
      hygraphLocale,
      hygraphSite,
      language,
      pathname,
      isDraft,
      routeArray,
      section,
      melwaysLocale,
      fullURL,
    } = config;
    const slugName = isArticlePreview ? 'article-preview' : 'article';
    const { sections } = getRouteInfo(routeArray, slugName);
    const { slug } = params;

    // if the Draft content needs to be displayed, need to use Token
    const previewProps = getPreviewProps(isDraft);
    const { stage, queryOptions } = previewProps;

    const variables = {
      where: {
        sites_some: { name: hygraphSite },
        // if multi locales article cannot be found, the reason could be the section name's localisation.
        // Check the locale value of section name, if not matching with the sectionDefault value, no article returns
        // For example; hiring-advice's Thai locale is different, so that locale value must be sent
        category: {
          relatedCategorySection: getSectionQuery(sections.reverse()),
        },
        ...filterDocumentBy(isArticlePreview, slug),
      },
      locale: getHygraphLocales(hygraphLocale),
      localesWithDefault: [hygraphLocale, Locale.Default],
      stage,
    };

    // Fetch the article if it exists.
    const { data, error } = await client.query<GetArticlesQuery>({
      query: GetArticlesDocument,
      variables,
      ...queryOptions,
    });

    if (error) {
      throw internalError(error);
    }

    // If the content is not available, redirect user to NotFound Page (CU-762).
    if (!data.articles.length) {
      throw notFound(slug);
    }

    const article = getArticleFromData(data, hygraphLocale);
    // If the content is not available, redirect to NotFound Page (CU-762).
    if (!article || isContentNA(article.content?.text)) {
      throw notFound(slug);
    }
    // ensure we have article now
    const locales = getLocales(
      site,
      pathname.replace(`/${language}/`, ''),
      data.articles[0].sites,
      data.articles[0].localizations,
    );
    // If the article is under about section, eg: about/news, load the data for subscription form and contact us

    const aboutSubFormContactUs = await getAboutSubForm_ContactUs(
      section,
      {
        hygraphSite,
        hygraphLocale,
        client,
      },
      previewProps,
    );
    const isTAL = article.category?.slug === 'talent-attraction-advice';
    const additionalData = {
      datalabSurvey: isTAL
        ? await loadDatalabData({
            fullURL,
            clientTAL,
            locale: melwaysLocale,
            request,
            requireSurveyResult: false,
          })
        : undefined,
    };

    if (Boolean(aboutSubFormContactUs)) {
      const updatedArticle = {
        ...article,
        ...aboutSubFormContactUs,
        additionalData: {
          ...additionalData,
        },
      };
      return json({ ...updatedArticle, locales });
    }

    return json({
      ...article,
      additionalData: {
        ...additionalData,
      },
      locales,
    });
  };

const filterDocumentBy = (
  isPreviewMode: boolean,
  documentFilterParam: string | undefined,
) => {
  if (isPreviewMode) {
    // Query hygraph - for article preview
    return { id: decodeURIComponent(documentFilterParam || '') };
  }
  // Query hygraph - for regular view
  return { slug: decodeURIComponent(documentFilterParam || '') };
};
